import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft, Lock, User, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const { admin, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [usernameData, setUsernameData] = useState({
    new_username: '',
    password: ''
  });
  
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [loadingUsername, setLoadingUsername] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoadingPassword(true);
    try {
      await axios.put(
        `${API}/admin/change-password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed! Please log in again with your new password.');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      // Force re-login so user must verify new credentials work
      setTimeout(() => {
        logout();
        navigate('/admin/login');
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoadingPassword(false);
    }
  };

  const handleUsernameChange = async (e) => {
    e.preventDefault();
    
    if (usernameData.new_username.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }
    
    setLoadingUsername(true);
    try {
      await axios.put(
        `${API}/admin/change-username`,
        usernameData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Username changed to "${usernameData.new_username}"! Please log in with your new username.`);
      const savedNewUsername = usernameData.new_username;
      setUsernameData({ new_username: '', password: '' });
      // Force re-login so the user uses the new username right away
      setTimeout(() => {
        logout();
        navigate('/admin/login');
      }, 1800);
      // (savedNewUsername kept in scope only for the toast message above)
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change username');
    } finally {
      setLoadingUsername(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="p-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
              <p className="text-sm text-gray-600 mt-1">Manage your login credentials</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Current Account Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="w-5 h-5 text-amber-600" />
                <span>Current Account</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Logged in as: <span className="font-semibold text-gray-900">{admin?.username}</span>
              </p>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Lock className="w-5 h-5 text-amber-600" />
                <span>Change Password</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <Input
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    required
                    placeholder="Enter current password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <Input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    required
                    placeholder="Enter new password (min 6 characters)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <Input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    required
                    placeholder="Confirm new password"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="bg-amber-800 hover:bg-amber-900"
                  disabled={loadingPassword}
                >
                  {loadingPassword ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Changing...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Change Password</>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Change Username */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="w-5 h-5 text-amber-600" />
                <span>Change Username</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUsernameChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Username
                  </label>
                  <Input
                    type="text"
                    value={usernameData.new_username}
                    onChange={(e) => setUsernameData({ ...usernameData, new_username: e.target.value })}
                    required
                    placeholder="Enter new username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm with Password
                  </label>
                  <Input
                    type="password"
                    value={usernameData.password}
                    onChange={(e) => setUsernameData({ ...usernameData, password: e.target.value })}
                    required
                    placeholder="Enter your password to confirm"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="bg-amber-800 hover:bg-amber-900"
                  disabled={loadingUsername}
                >
                  {loadingUsername ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Changing...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Change Username</>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminSettings;
